import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Share2, Copy, MessageCircle, Twitter, Facebook, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { createWatermarkedAvatar } from "@/utils/imageWatermark";
import {
  fetchTrackShareMeta,
  fetchProfileShareMeta,
  buildShareSuffix,
  type TrackShareMeta,
} from "@/hooks/useTrackShareMeta";
import { logSponsoredShare } from "@/utils/sponsoredShareTracking";

interface ShareButtonProps {
  url: string;
  title: string;
  text: string;
  imageUrl?: string;
  artistName?: string;
  username?: string;
  sponsors?: Array<{ avatar_url?: string | null; display_name: string }>;
  roleLabel?: string;
  isVerified?: boolean;
  /** When provided, fetches sponsor/artist attribution + hashtags and logs share events. */
  trackId?: string;
  /** When provided (and trackId isn't), fetches this artist/sponsor profile's own saved hashtags. */
  profileId?: string | null;
  /** Fallback artist id for share-event attribution when meta.source !== "artist". */
  artistId?: string | null;
  className?: string;
  variant?: "default" | "outline" | "ghost" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
}

const SHARE_HASHTAGS =
  "#PayStreem #SupportIndependentArtists #PoweredByFans #NaijaMusic #Afrobeats #NigerianMusic #IndieArtist #SupportThisArtist #DiscoverNewMusic";

/** Build the trailing hashtag/sponsor block. Falls back to default hashtags when no meta. */
function buildSuffix(meta: TrackShareMeta | null): string {
  if (meta) {
    const sponsorSuffix = buildShareSuffix(meta);
    if (sponsorSuffix) return sponsorSuffix;
  }
  return SHARE_HASHTAGS;
}

export function ShareButton({
  url,
  title,
  text,
  imageUrl,
  artistName,
  username,
  sponsors,
  roleLabel,
  isVerified,
  trackId,
  profileId,
  artistId,
  className = "",
  variant = "outline",
  size = "sm",
}: ShareButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const metaRef = useRef<TrackShareMeta | null>(null);

  const resolveMeta = async (): Promise<TrackShareMeta | null> => {
    try {
      if (trackId) return await fetchTrackShareMeta(trackId);
      if (profileId) return await fetchProfileShareMeta(profileId);
      return null;
    } catch {
      return null;
    }
  };

  // Prefetch sponsor/artist hashtags up front so platform intent links (opened
  // synchronously from a click, to avoid popup blockers) already have them.
  useEffect(() => {
    let cancelled = false;
    resolveMeta().then((m) => {
      if (!cancelled) metaRef.current = m;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId, profileId]);

  const logShare = (target: string) => {
    if (trackId) {
      logSponsoredShare({
        trackId,
        meta: metaRef.current,
        method: "web_target",
        target,
        artistId: artistId ?? null,
      });
    }
  };

  const openIntent = (shareUrl: string, target: string) => {
    window.open(shareUrl, "_blank", "noopener,noreferrer");
    logShare(target);
    setIsOpen(false);
  };

  const shareToTwitter = () => {
    const encodedText = encodeURIComponent(`${text}\n\n${buildSuffix(metaRef.current)}`);
    const encodedUrl = encodeURIComponent(url);
    openIntent(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, "twitter");
  };

  const shareToWhatsApp = () => {
    const encodedText = encodeURIComponent(`${text}\n\n${buildSuffix(metaRef.current)}`);
    const encodedUrl = encodeURIComponent(url);
    openIntent(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, "whatsapp");
  };

  const shareToFacebook = () => {
    const encodedUrl = encodeURIComponent(url);
    openIntent(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, "facebook");
  };

  const shareToTelegram = () => {
    const encodedText = encodeURIComponent(`${text}\n\n${buildSuffix(metaRef.current)}`);
    const encodedUrl = encodeURIComponent(url);
    openIntent(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, "telegram");
  };

  const shareViaEmail = () => {
    const encodedText = encodeURIComponent(`${text}\n\n${buildSuffix(metaRef.current)}`);
    const encodedUrl = encodeURIComponent(url);
    openIntent(`mailto:?subject=${encodeURIComponent(title)}&body=${encodedText}%20${encodedUrl}`, "email");
  };

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      const meta = await resolveMeta();
      const suffix = buildSuffix(meta);
      const shareData: ShareData = {
        title: title,
        text: `${text}\n\n${suffix}`,
        url: url,
      };

      if (imageUrl && artistName && username && navigator.canShare) {
        try {
          const watermarkedBlob = await createWatermarkedAvatar(
            imageUrl,
            artistName,
            username,
            sponsors,
            roleLabel,
            isVerified,
          );
          const file = new File([watermarkedBlob], "watermarked-profile.jpg", {
            type: "image/jpeg",
          });
          if (navigator.canShare({ files: [file] })) {
            shareData.files = [file];
          }
        } catch {
          try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const file = new File([blob], "profile-image.jpg", {
              type: blob.type,
            });
            if (navigator.canShare({ files: [file] })) {
              shareData.files = [file];
            }
          } catch {
            // ignore
          }
        }
      } else if (imageUrl && navigator.canShare) {
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const file = new File([blob], "profile-image.jpg", {
            type: blob.type,
          });
          if (navigator.canShare({ files: [file] })) {
            shareData.files = [file];
          }
        } catch {
          // ignore
        }
      }

      await navigator.share(shareData);
      if (trackId) {
        logSponsoredShare({
          trackId,
          meta,
          method: "native",
          artistId: artistId ?? null,
        });
      }
      toast.success("Shared successfully!");
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        console.error("Error sharing:", error);
        toast.error("Failed to share");
      }
    }
  };

  const copyToClipboard = async () => {
    try {
      const meta = await resolveMeta();
      const suffix = buildSuffix(meta);
      const shareText = `${text}\n\n🎵 ${title}\n${url}\n\n${suffix}`;

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareText);
        toast.success("Link copied to clipboard!");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = shareText;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand("copy");
          toast.success("Link copied to clipboard!");
        } catch {
          toast.error("Failed to copy link");
        }
        document.body.removeChild(textArea);
      }

      if (trackId) {
        logSponsoredShare({
          trackId,
          meta,
          method: "copy",
          artistId: artistId ?? null,
        });
      }
    } catch {
      toast.error("Failed to copy link");
    }
    setIsOpen(false);
  };

  const buttonText = size === "icon" ? "" : "Share";
  const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          title="Share"
          aria-label="Share"
          className={`flex items-center gap-2 ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Share2 className="h-4 w-4" />
          {buttonText}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel>Share</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hasNativeShare && (
          <>
            <DropdownMenuItem onClick={handleNativeShare} className="cursor-pointer">
              <Share2 className="w-4 h-4 mr-2" />
              Device share…
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={shareToWhatsApp} className="cursor-pointer">
          <MessageCircle className="w-4 h-4 mr-2" />
          WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareToTwitter} className="cursor-pointer">
          <Twitter className="w-4 h-4 mr-2" />
          X (Twitter)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareToFacebook} className="cursor-pointer">
          <Facebook className="w-4 h-4 mr-2" />
          Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareToTelegram} className="cursor-pointer">
          <Send className="w-4 h-4 mr-2" />
          Telegram
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareViaEmail} className="cursor-pointer">
          <Mail className="w-4 h-4 mr-2" />
          Email
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={copyToClipboard} className="cursor-pointer">
          <Copy className="w-4 h-4 mr-2" />
          Copy Link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
